import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Settings, RefreshCw, Download } from 'lucide-react';
import EnhancedMessage from './ui/EnhancedMessage';
import EnhancedChatInput from './ui/EnhancedChatInput';
import MessageActions from './ui/MessageActions';
import { storage } from '../utils/storage';
import { streamingChat } from '../utils/streamingChat';

interface EnhancedThinkMateChatProps {
  initialQuestion?: string;
  onClose: () => void;
}



const EnhancedThinkMateChat: React.FC<EnhancedThinkMateChatProps> = ({ 
  initialQuestion, 
  onClose 
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialSentRef = useRef(false);
  const [showSettings, setShowSettings] = useState(false);
  const [messages, setMessages] = useState<Array<{ id: string; role: 'user' | 'assistant'; content: string }>>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPersonalized, setIsPersonalized] = useState<boolean | null>(null);
  const [isAnalyzingData, setIsAnalyzingData] = useState(false);

  // Get user context for personalization
  const onboardingData = storage.getOnboardingData<{ name?: string }>({});
  const mentorStyle = 'supportive';

  // Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = useCallback(async (overrideText?: string) => {
    const text = overrideText ?? input;
    if (!text.trim() || isLoading) return;
    
    // Add user message
    const userMessage = { id: `user-${Date.now()}`, role: 'user' as const, content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    
    setIsLoading(true);
    setIsAnalyzingData(true);
    
    try {
      // Use conversation history for context (exclude system messages)
      const conversationMessages = messages.concat(userMessage).map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content
      }));
      
      await streamingChat(conversationMessages, {
        usePersonalizedEndpoint: true, // Use personalized AI endpoint with user data
        onPersonalizationStatus: (personalized: boolean) => {
          setIsPersonalized(personalized);
          setIsAnalyzingData(false);
        },
        onPartialUpdate: (text) => {
          // Update the assistant message with streaming content
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              lastMessage.content = text;
            } else {
              newMessages.push({ id: `assistant-${Date.now()}`, role: 'assistant', content: text });
            }
            return newMessages;
          });
        },
        onComplete: (fullText) => {
          // Final update when streaming is complete
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              lastMessage.content = fullText;
            } else {
              newMessages.push({ id: `assistant-${Date.now()}`, role: 'assistant', content: fullText });
            }
            return newMessages;
          });
          setIsLoading(false);
          setIsAnalyzingData(false);
        },
        onError: (error) => {
          console.error('Streaming chat error:', error);
          setMessages(prev => [...prev, { id: `assistant-${Date.now()}`, role: 'assistant', content: 'Sorry, there was an error getting a response. Please try again.' }]);
          setIsLoading(false);
          setIsAnalyzingData(false);
          setIsPersonalized(false);
        }
      });
    } catch (error) {
      console.error('Streaming chat failed:', error);
      setMessages(prev => [...prev, { id: `assistant-${Date.now()}`, role: 'assistant', content: 'Sorry, there was an error getting a response. Please try again.' }]);
      setIsLoading(false);
      setIsAnalyzingData(false);
      setIsPersonalized(false);
    }
  }, [input, isLoading, messages]);

  // If initialQuestion is provided, send it as the first message
  useEffect(() => {
    if (initialQuestion && messages.length === 0 && !initialSentRef.current) {
      initialSentRef.current = true;
      handleSendMessage(initialQuestion);
    }
  }, [initialQuestion, messages.length, handleSendMessage]);

  const handleReaction = useCallback((messageId: string, emoji: string, action: 'add' | 'remove') => {
    // You can implement reaction persistence here
    console.log(`Reaction ${action}ed: ${emoji} for message ${messageId}`);
  }, []);

  const handleCopyMessage = useCallback((content: string) => {
    navigator.clipboard.writeText(content);
    // You can add a toast notification here
  }, []);

  const handleReportMessage = useCallback((messageId: string) => {
    // You can implement report functionality here
    console.log(`Message reported: ${messageId}`);
  }, []);

  const exportChatHistory = useCallback(() => {
    const history = messages.map(msg => ({
      role: msg.role,
      content: msg.content,
      timestamp: new Date().toISOString(),
    }));
    
    const blob = new Blob([JSON.stringify(history, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `thinkmate-chat-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [messages]);

  const clearChat = useCallback(() => {
    // You can implement chat clearing functionality here
    console.log('Chat cleared');
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-orange-50 to-yellow-50 p-6 md:p-4">
      <div className="min-h-full flex items-center justify-center">
        <div className="rounded-2xl shadow-xl w-full max-w-2xl bg-white p-8 md:p-6 space-y-6 max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-between">
              <button
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-all duration-200"
                onClick={onClose}
                aria-label="Back to dashboard"
              >
                <ChevronLeft size={24} />
              </button>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={exportChatHistory}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-all duration-200"
                  title="Export chat history"
                >
                  <Download size={20} />
                </button>
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className={`p-2 rounded-full transition-all duration-200 ${
                    showSettings 
                      ? 'bg-gray-100 text-gray-700' 
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Chat settings"
                >
                  <Settings size={20} />
                </button>
              </div>
            </div>
            
            <div>
              <h1 className="text-2xl font-bold text-text-primary mb-2">ThinkMate Chat</h1>
              <div className="flex items-center justify-center gap-2 mb-2">
                <p className="text-text-secondary text-sm">Your AI accountability partner</p>
                {isPersonalized === true && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    📊 Personalized
                  </span>
                )}
                {isPersonalized === false && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    🤖 General
                  </span>
                )}
                {isAnalyzingData && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    🔄 Analyzing...
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Settings Panel */}
          <AnimatePresence>
            {showSettings && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <h3 className="font-semibold text-gray-800 mb-2">Chat Settings</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>• Coaching Style: {mentorStyle}</p>
                  <p>• User: {onboardingData?.name || 'Anonymous'}</p>
                  <p>• Messages: {messages.length}</p>
                  <p>• Personalization: {
                    isPersonalized === true ? '✅ Using your data' : 
                    isPersonalized === false ? '⚠️ General responses' : 
                    '🔄 Analyzing...'
                  }</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
            <AnimatePresence initial={false}>
              {messages.map((msg, idx) => (
                <div key={msg.id} className="relative group">
                  <EnhancedMessage
                    content={msg.content}
                    role={msg.role === 'user' ? 'user' : 'assistant'}
                    isStreaming={isLoading && idx === messages.length - 1}
                  />
                  
                  {/* Message Actions - Show on hover for assistant messages */}
                  {msg.role === 'assistant' && (
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <MessageActions
                        messageId={msg.id}
                        onReaction={(emoji, action) => handleReaction(msg.id, emoji, action)}
                        onCopy={handleCopyMessage}
                        onReport={() => handleReportMessage(msg.id)}
                      />
                    </div>
                  )}
                </div>
              ))}
              
              {isLoading && messages.length === 0 && (
                <motion.div 
                  className="flex justify-center items-center py-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="flex items-center gap-2 text-orange-600">
                    <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                    <span>{isAnalyzingData ? 'Reviewing your patterns...' : 'Thinking...'}</span>
                  </div>
                </motion.div>
              )}
              
              {isAnalyzingData && messages.length > 0 && (
                <motion.div 
                  className="flex justify-center items-center py-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                    <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <span>Reviewing your patterns...</span>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </AnimatePresence>
          </div>

          {/* Chat Input */}
          <div className="border-t border-gray-200 pt-4">
            <EnhancedChatInput
              value={input}
              onChange={setInput}
              onSend={handleSendMessage}
              isLoading={isLoading}
              placeholder="Ask ThinkMate anything... (Shift+Enter for new line)"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedThinkMateChat; 